import React from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import type { xplatData, Lot, Amenities, TView, SliderImage } from '../types';
import { STRINGS } from '../constants';

type Position = {
  x: number;
  y: number;
};

type SliderDataType = {
  images: SliderImage[];
  startIndex: number;
} | null;

export type TScreen = "sendInquiry" | null;


interface XplatContextValue {
  platData: xplatData | null;
  setPlatData: (platData: xplatData | null) => void;
  lotData: Lot[];
  setLotData: React.Dispatch<React.SetStateAction<Lot[]>>;
  amenitiesData: Amenities[];
  setAmenitiesData: React.Dispatch<React.SetStateAction<Amenities[]>>;
  loading: boolean | false;
  setLoading: (loading: boolean | false) => void;

  zoomValue: number;
  setZoomValue: React.Dispatch<React.SetStateAction<number>>;
  position: Position;
  setPosition: React.Dispatch<React.SetStateAction<Position>>;
  searchTerm: string | null; 
	setSearchTerm: (term: string | null) => void;
  selectedLotId: string | null; 
	setSelectedLotId: (lotId: string | null) => void;
  view: TView;
  setView: React.Dispatch<React.SetStateAction<TView>>;
  sliderData: SliderDataType;
  setSliderData: React.Dispatch<React.SetStateAction<SliderDataType>>;

  screen: TScreen;
	setScreen: React.Dispatch<React.SetStateAction<TScreen>>;
}

const XplatContext = createContext<XplatContextValue | undefined>(undefined);

export const XplatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [platData, setPlatData] = useState<xplatData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lotData, setLotData] = useState<Lot[]>([]);
  const [amenitiesData, setAmenitiesData] = useState<Amenities[]>([]);
  // zoom 
  const [zoomValue, setZoomValue] = useState<number>(100);
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [view, setView] = useState<TView>("details");
  const [sliderData, setSliderData] = useState<SliderDataType>(null);
  const [selectedLotId, setSelectedLotId] = useState<string | null>(null);
  const [screen, setScreen] = useState<TScreen>(null);

  const value = useMemo(() => ({ 
    platData,
    setPlatData,
    loading,
    setLoading,
    lotData,
    setLotData,
    amenitiesData,
    setAmenitiesData,
    zoomValue, setZoomValue,
    position, setPosition,
    searchTerm,
		setSearchTerm,
    view, 
    setView,
    sliderData, 
    setSliderData,
    selectedLotId,
    setSelectedLotId,
    screen, setScreen
  }),
   [platData, loading, lotData, amenitiesData, zoomValue, setZoomValue, position, setPosition, searchTerm, setSearchTerm, 
    view, setView, 
    sliderData, setSliderData,
    selectedLotId, setSelectedLotId,
    screen, setScreen
  ]);

  return <XplatContext.Provider value={value}>{children}</XplatContext.Provider>;
};

export const useXplatContext = (): XplatContextValue => {
  const ctx = useContext(XplatContext);
  if (!ctx) throw new Error(STRINGS.errors.xplatContextOutOfProvider);
  return ctx;
};
